// @story HIR-HR5
// Sending an offer: choose the reply-by date (7 days unless changed), then send.
// The message goes through Messages; if no template is mapped for the Offer
// step the server says so and nothing is sent.
import React, { useState } from 'react';
import { X, Send, Loader2 } from 'lucide-react';
import { offerLetterApi } from '../../../services/offerLetter.service';
import { isoDateInIndia, DEFAULT_DEADLINE_DAYS } from './offerDates';

export default function SendOfferDialog({ resumeId, candidateName, onClose, onSent }) {
  const defaultDate = isoDateInIndia(DEFAULT_DEADLINE_DAYS);
  const [date, setDate] = useState(defaultDate);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const send = async () => {
    setBusy(true); setError('');
    try {
      // Untouched → let the server apply the default (exactly 7 days after sending).
      const r = await offerLetterApi.sendOffer(resumeId, date === defaultDate ? undefined : date);
      onSent && onSent(r);
      onClose();
    } catch (e) {
      setError(e?.message || 'Could not send the offer.');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold text-gray-900">Send offer</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100" aria-label="Close"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          {candidateName || 'The candidate'} will get the offer by email and WhatsApp, with a link to accept or decline.
        </p>
        <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="offer-deadline">Reply by</label>
        <input
          id="offer-deadline"
          type="date"
          value={date}
          min={isoDateInIndia(1)}
          max={isoDateInIndia(90)}
          onChange={(e) => setDate(e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
        />
        <p className="text-xs text-gray-500 mt-1">
          {date === defaultDate
            ? `The usual ${DEFAULT_DEADLINE_DAYS} days. If there is no answer by then, the offer lapses and the next candidate is put in front of you.`
            : 'Until the end of this day. If there is no answer by then, the offer lapses and the next candidate is put in front of you.'}
        </p>
        {error && <p className="text-sm text-red-600 mt-3">{error}</p>}
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-3 py-2 text-sm rounded-lg text-gray-600 hover:bg-gray-100">Cancel</button>
          <button onClick={send} disabled={busy || !date}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {busy ? 'Sending…' : 'Send offer'}
          </button>
        </div>
      </div>
    </div>
  );
}
